// axiosInstance
import axios from "axios";
import { BrowserStorage } from "./browserStorage";
import {
  authUpdatedEvent,
  sessionExpiryKey,
  sessionExpiryTime,
  sessionExtendedEvent,
  sessionExtensionMarkerKey,
  sessionExtensionThrottleMs,
  sessionKey,
} from "../Constants/authConfig";
import { getEnv } from "../Constants/env";

const getAuthTokenFromSession = (): string | null => {
  const raw = BrowserStorage.getSessionItem(sessionKey);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return parsed?.idToken ?? parsed?.accessToken ?? null;
  } catch {
    return null;
  }
};

const toBearerToken = (token: string): string => {
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
};

const parseTimestamp = (value: string | null): number | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "number" ? parsed : null;
  } catch {
    return null;
  }
};

const extendSessionIfNeeded = () => {
  const now = Date.now();
  const lastExtendedAt = parseTimestamp(
    BrowserStorage.getSessionItem(sessionExtensionMarkerKey),
  );

  if (
    lastExtendedAt !== null &&
    now - lastExtendedAt < sessionExtensionThrottleMs
  ) {
    return;
  }

  BrowserStorage.setSessionItem(
    sessionExpiryKey,
    JSON.stringify(now + sessionExpiryTime),
  );
  BrowserStorage.setSessionItem(sessionExtensionMarkerKey, JSON.stringify(now));

  window.dispatchEvent(
    new CustomEvent(sessionExtendedEvent, {
      detail: { timestamp: now },
    }),
  );
};

let isLogoutRedirectInProgress = false;

const isExplicitDeny403 = (error: any) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.message ?? "").toLowerCase();

  return status === 403 && message.includes("explicit deny");
};

const logoutAndRedirectToLogin = () => {
  if (isLogoutRedirectInProgress) {
    return;
  }

  isLogoutRedirectInProgress = true;
  BrowserStorage.clear(sessionKey);
  BrowserStorage.clear(sessionExpiryKey);
  BrowserStorage.clear(sessionExtensionMarkerKey);

  window.dispatchEvent(new CustomEvent(authUpdatedEvent));

  const loginUrl = getEnv()?.redirectUri ?? window.location.origin;
  window.location.assign(loginUrl);
};

const handleResponseSuccess = (response: any) => {
  // extendSessionIfNeeded();
  return response;
};

const handleResponseError = (error: any) => {
  if (isExplicitDeny403(error)) {
    logoutAndRedirectToLogin();
  }

  return Promise.reject(error);
};

export const axiosInstance = axios.create({
  baseURL: `${getEnv()?.baseURL}`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Some third-party components may call the default axios client directly.
// Registering a global response interceptor ensures session extension still occurs.
if (axios.interceptors?.response?.use) {
  axios.interceptors.response.use(handleResponseSuccess, handleResponseError);
}

axiosInstance.interceptors.request.use((config) => {
  const token = getAuthTokenFromSession();

  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = toBearerToken(token);
  }

  return config;
});

axiosInstance.interceptors.response.use(
  handleResponseSuccess,
  handleResponseError,
);
